import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { fmtPrice } from "../lib/api";
import { handleImgError } from "../lib/imgFallback";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";

const FREE_SHIP = 250;

export default function CartSheet() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, unitPriceOf } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    if (items.length === 0) return;
    setOpen(false);
    navigate("/checkout");
  };

  const remaining = Math.max(0, FREE_SHIP - subtotal);
  const pct = Math.min(100, (subtotal / FREE_SHIP) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-sheet" className="bg-[#0F0F10] border-l border-white/10 text-[#F5F5F5] w-full sm:max-w-md flex flex-col">
        <SheetHeader className="border-b border-white/10 pb-5">
          <SheetTitle className="font-serif-editorial text-3xl text-[#F5F5F5]">Your Cart</SheetTitle>
          <SheetDescription className="text-[11px] uppercase tracking-[0.18em] text-[#A8A8A8]">Research use only · Not for human consumption</SheetDescription>
        </SheetHeader>

        {items.length > 0 && (
          <div className="pt-5">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-[#A8A8A8] mb-2">
              <span>{remaining > 0 ? `Add ${fmtPrice(remaining)} for free shipping` : "Free shipping unlocked"}</span>
            </div>
            <div className="h-px bg-white/10 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-[#4ADE80] transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto py-5 space-y-4">
          {items.length === 0 && (
            <div data-testid="empty-cart-msg" className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-[13px] uppercase tracking-[0.18em] text-[#A8A8A8]">Your cart is empty.</p>
            </div>
          )}
          {items.map((i) => (
            <div key={i.slug} data-testid={`cart-item-${i.slug}`} className="flex gap-4 border-b border-white/[0.06] pb-4">
              <img src={i.image} alt={i.name} onError={handleImgError} className="h-20 w-20 object-cover bg-[#0F0F10] border border-white/10" />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                  <p className="font-display text-sm truncate">{i.name}</p>
                  <button data-testid={`remove-${i.slug}`} onClick={() => removeItem(i.slug)} aria-label="Remove item">
                    <Trash2 className="h-4 w-4 text-[#A8A8A8] hover:text-[#4ADE80] transition-colors" />
                  </button>
                </div>
                <p className="text-[11px] text-[#A8A8A8] font-light">{i.size}</p>
                <div className="flex justify-between items-center mt-3">
                  <div className="flex items-center border border-white/15">
                    <button data-testid={`dec-${i.slug}`} onClick={() => updateQty(i.slug, i.quantity - 1)} className="p-1.5 hover:text-[#4ADE80]"><Minus className="h-3 w-3" /></button>
                    <span className="px-3 text-sm">{i.quantity}</span>
                    <button data-testid={`inc-${i.slug}`} onClick={() => updateQty(i.slug, i.quantity + 1)} className="p-1.5 hover:text-[#4ADE80]"><Plus className="h-3 w-3" /></button>
                  </div>
                  <span className="font-display text-sm">{fmtPrice(unitPriceOf(i) * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-5 space-y-5">
          <div className="flex justify-between items-baseline">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#A8A8A8]">Subtotal</span>
            <span data-testid="cart-subtotal" className="font-display text-2xl">{fmtPrice(subtotal)}</span>
          </div>
          <button
            data-testid="checkout-btn"
            disabled={items.length === 0}
            onClick={goToCheckout}
            className="btn-press w-full bg-[#4ADE80] text-[#0F0F10] uppercase tracking-[0.18em] text-[12px] py-4 hover:bg-[#6FE998] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            Checkout <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
